import { useState } from 'react'
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  FileButton,
  Group,
  Image,
  Paper,
  Stack,
  Text,
} from '@mantine/core'
import {
  IconAlertTriangle, IconCrop, IconFocusCentered, IconPhotoPlus, IconTrash,
} from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { ImageCropDialog } from '../components/assets/ImageCropDialog'
import { FocusPointPicker } from '../components/forms/FocusPointPicker'
import { useImageMediaField } from './useImageMediaField'

export function ImageEditorField(props) {
  const { t } = useTranslation()
  const { field, error } = props
  const {
    media, target, accept, sourceUrl, previewUrl, cropMode, cropUseful, imageWarning,
    localError, cropOpened, setCropOpened, chooseFile, applyCrop, clear,
    focalPoint, focusUseful, setFocalPoint,
  } = useImageMediaField(props)
  const [focusOpened, setFocusOpened] = useState(false)

  return (
    <Stack gap={6}>
      <Text size="sm" fw={500}>{field.label}</Text>
      <Paper withBorder radius="md" p="sm">
        {media.sourceAssetId ? (
          <Group wrap="nowrap" align="center">
            <Image src={previewUrl || sourceUrl} alt="" w={104} h={72} fit="contain" radius="sm" bg="gray.0" />
            <Box style={{ flex: 1, minWidth: 0 }}>
              <Text size="xs" fw={600} truncate>
                {media.crop || media.renditionAssetId || !cropMode ? t('editorV2.form.fields.imageReady') : t('editorV2.form.fields.cropRequired')}
              </Text>
              <Text size="xs" c="dimmed">
                {t('editorV2.form.fields.renditions', { width: target?.width, height: target?.height })}
              </Text>
              <Group gap={6} mt={6}>
                {cropUseful && sourceUrl && (
                  <Button
                    size="compact-xs"
                    variant="light"
                    leftSection={<IconCrop size={14} />}
                    onClick={() => setCropOpened(true)}
                  >
                    {t('editorV2.form.fields.adjustCrop')}
                  </Button>
                )}
                {focusUseful && sourceUrl && (
                  <Button
                    size="compact-xs"
                    variant={focusOpened ? 'filled' : 'light'}
                    leftSection={<IconFocusCentered size={14} />}
                    onClick={() => setFocusOpened((open) => !open)}
                  >
                    {focusOpened ? t('editorV2.form.common.done') : t('editorV2.form.fields.focusPoint')}
                  </Button>
                )}
                <FileButton accept={accept} onChange={chooseFile}>
                  {(buttonProps) => <Button {...buttonProps} size="compact-xs" variant="default">{t('editorV2.form.common.replace')}</Button>}
                </FileButton>
              </Group>
            </Box>
            <ActionIcon
              color="red"
              variant="light"
              aria-label={t('editorV2.form.common.removeLabel', { label: field.label })}
              onClick={clear}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        ) : (
          <FileButton accept={accept} onChange={chooseFile}>
            {(buttonProps) => (
              <Button {...buttonProps} fullWidth size="xs" variant="light" leftSection={<IconPhotoPlus size={16} />}>
                {cropMode ? t('editorV2.form.fields.uploadAndCrop') : t('editorV2.form.fields.uploadAndOptimize')}
              </Button>
            )}
          </FileButton>
        )}
      </Paper>
      {focusOpened && sourceUrl && (
        <Paper withBorder radius="md" p="sm">
          <FocusPointPicker src={sourceUrl} focalPoint={focalPoint} onPick={setFocalPoint} />
        </Paper>
      )}
      {field.help && <Text size="xs" c="dimmed">{field.help}</Text>}
      {imageWarning && (
        <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />} py={6}>
          <Text size="xs">{imageWarning}</Text>
        </Alert>
      )}
      {(localError || error) && <Text size="xs" c="red">{localError || error}</Text>}
      {cropMode && target && (
        <ImageCropDialog
          opened={cropOpened}
          onClose={() => setCropOpened(false)}
          onApply={applyCrop}
          sourceUrl={sourceUrl}
          crop={media.crop}
          target={target}
          label={field.label}
        />
      )}
    </Stack>
  )
}
